/**
 * Partner Offers Service
 * CRUD operations for partner offers and categories
 */

import { supabase } from "./supabase";
import { fetchAllRows } from "../utils/fetchAllRows";
import {
  partnerOfferFilterOps,
  compareByEffectiveCompany,
} from "../utils/partnerOfferFilters";
import type { UUID } from "../types/common.types";
import type {
  PartnerOffer,
  PartnerOfferCategory,
  PartnerOfferFormData,
  PartnerOfferFilters,
  PartnerOfferSort,
  PartnerOfferEvent,
  PartnerOfferApiKey,
  StateInsuranceClassification,
  PartnerOfferStatus,
} from "../types/partnerOffer.types";

// =====================================================
// OFFER CRUD
// =====================================================

const OFFER_SELECT = `
      *,
      category:partner_offer_categories(id, name, slug),
      source_organization:organizations!partner_offers_source_organization_id_fkey(id, name)
    `;

// Build a filtered+ordered partner_offers query (shared by list + export).
// Filter semantics live in ../utils/partnerOfferFilters (pure + unit-tested).
// A stable secondary sort by `id` is ALWAYS appended so offset-based pagination
// (the export) can't return tied rows in different orders across a page boundary
// (fields like company_name / start_date / end_date now hold many NULLs/dupes).
function buildPartnerOffersQuery(
  filters?: PartnerOfferFilters,
  sort?: PartnerOfferSort,
  matchingSourceOrgIds?: string[],
) {
  let query = supabase
    .from("partner_offers")
    .select(OFFER_SELECT)
    .order(sort?.field || "created_at", {
      ascending: sort?.ascending ?? false,
    })
    .order("id", { ascending: true });
  for (const { op, args } of partnerOfferFilterOps(
    filters,
    matchingSourceOrgIds,
  )) {
    if (op === "eq") {
      query = query.eq(args[0], args[1] as string | number | boolean);
    } else if (op === "is") {
      query = query.is(args[0], args[1] as null);
    } else if (op === "or") {
      query = query.or(args[0] as string);
    } else if (op === "contains") {
      query = query.contains(args[0], args[1] as string[]);
    }
  }
  return query;
}

// Resolve organization ids whose name matches the search term, so a search for
// the DISPLAYED partner name finds offers that derive their identity from the
// source org (company_name = NULL). Returns undefined when there's no search.
async function resolveMatchingSourceOrgIds(
  search?: string,
): Promise<string[] | undefined> {
  if (!search) {
    return undefined;
  }
  const { data } = await supabase
    .from("organizations")
    .select("id")
    .ilike("name", `%${search}%`)
    .limit(500);
  return (data ?? []).map((o) => o.id as string);
}

// The "Company" column can't be ORDER BY'd server-side (its effective value is
// the joined source-org name for company_name = NULL rows), so we sort the
// fetched set by the effective company name here.
function sortByEffectiveCompanyIfNeeded(
  rows: PartnerOffer[],
  sort?: PartnerOfferSort,
): PartnerOffer[] {
  if (sort?.field !== "company_name") {
    return rows;
  }
  return [...rows].sort((a, b) =>
    compareByEffectiveCompany(a, b, sort.ascending),
  );
}

/**
 * Get all partner offers with optional filtering and sorting.
 * NOTE: single unpaged request — subject to PostgREST's 1000-row cap. For a
 * complete export use getPartnerOffersForExport() (paginated).
 */
export async function getPartnerOffers(
  filters?: PartnerOfferFilters,
  sort?: PartnerOfferSort,
): Promise<PartnerOffer[]> {
  const matchingSourceOrgIds = await resolveMatchingSourceOrgIds(
    filters?.search,
  );
  const { data, error } = await buildPartnerOffersQuery(
    filters,
    sort,
    matchingSourceOrgIds,
  );

  if (error) {
    console.error("Error fetching partner offers:", error);
    throw error;
  }

  return sortByEffectiveCompanyIfNeeded((data || []) as PartnerOffer[], sort);
}

/**
 * Get ALL partner offers matching the filters, paginated past PostgREST's
 * 1000-row cap. Used by the CSV export so it never silently truncates.
 */
export async function getPartnerOffersForExport(
  filters?: PartnerOfferFilters,
  sort?: PartnerOfferSort,
): Promise<PartnerOffer[]> {
  const matchingSourceOrgIds = await resolveMatchingSourceOrgIds(
    filters?.search,
  );
  const rows = await fetchAllRows<PartnerOffer>(
    (from, to) =>
      buildPartnerOffersQuery(filters, sort, matchingSourceOrgIds).range(
        from,
        to,
      ) as unknown as PromiseLike<{
        data: PartnerOffer[] | null;
        error: unknown;
      }>,
  );
  return sortByEffectiveCompanyIfNeeded(rows, sort);
}

/**
 * Get a single partner offer by ID
 */
export async function getPartnerOffer(id: UUID): Promise<PartnerOffer | null> {
  const { data, error } = await supabase
    .from("partner_offers")
    .select(
      `
      *,
      category:partner_offer_categories(id, name, slug, parent_id),
      source_organization:organizations!partner_offers_source_organization_id_fkey(id, name)
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching partner offer:", error);
    throw error;
  }

  return data as PartnerOffer;
}

/**
 * Create a new partner offer
 */
export async function createPartnerOffer(
  formData: PartnerOfferFormData,
): Promise<PartnerOffer> {
  const { data, error } = await supabase
    .from("partner_offers")
    .insert({
      external_id: formData.external_id || null,
      // FR-03: company_name is now optional (Source Organization is the identity).
      company_name: formData.company_name || null,
      company_logo_url: formData.company_logo_url || null,
      headline: formData.headline,
      description: formData.description || null,
      short_description: formData.short_description || null,
      offer_amount: formData.offer_amount,
      offer_link: formData.offer_link || null,
      promo_code: formData.promo_code || null,
      legal_disclaimer: formData.legal_disclaimer || null,
      terms_and_conditions: formData.terms_and_conditions || null,
      redemption_instructions: formData.redemption_instructions || null,
      image_url: formData.image_url || null,
      category_id: formData.category_id || null,
      source_organization_id: formData.source_organization_id || null,
      tags: formData.tags || [],
      // FR-04/FR-05: Standing offers store NULL dates (evergreen); Lightning
      // offers carry real dates (form-enforced as required for lightning).
      offer_kind: formData.offer_kind,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      start_time: formData.start_time || "00:00:00",
      end_time: formData.end_time || "23:59:59",
      timezone: formData.timezone || "America/Chicago",
      brand_target: formData.brand_target,
      offer_type: formData.offer_type,
      target_states: formData.target_states || null,
      target_all_states: formData.target_all_states,
      is_featured: formData.is_featured,
      priority_score: formData.priority_score,
      status: formData.status,
      max_redemptions: formData.max_redemptions || null,
      redemptions_per_member: formData.redemptions_per_member || 1,
    })
    .select(
      `
      *,
      category:partner_offer_categories(id, name, slug),
      source_organization:organizations!partner_offers_source_organization_id_fkey(id, name)
    `,
    )
    .single();

  if (error) {
    console.error("Error creating partner offer:", error);
    throw error;
  }

  return data as PartnerOffer;
}

/**
 * Update a partner offer
 */
export async function updatePartnerOffer(
  id: UUID,
  formData: Partial<PartnerOfferFormData>,
): Promise<PartnerOffer> {
  // Normalize nullable fields: date columns reject '' and company_name (FR-03)
  // may now be blank. Only touch keys actually present on the partial so we
  // never clobber a field the caller didn't intend to change.
  const payload: Record<string, unknown> = { ...formData };
  if ("start_date" in payload) {
    payload.start_date = payload.start_date || null;
  }
  if ("end_date" in payload) {
    payload.end_date = payload.end_date || null;
  }
  if ("company_name" in payload) {
    payload.company_name = payload.company_name || null;
  }
  const { data, error } = await supabase
    .from("partner_offers")
    .update(payload)
    .eq("id", id)
    .select(
      `
      *,
      category:partner_offer_categories(id, name, slug),
      source_organization:organizations!partner_offers_source_organization_id_fkey(id, name)
    `,
    )
    .single();

  if (error) {
    console.error("Error updating partner offer:", error);
    throw error;
  }

  return data as PartnerOffer;
}

/**
 * Archive a partner offer (soft delete)
 */
export async function archivePartnerOffer(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("partner_offers")
    .update({ status: "archived" as PartnerOfferStatus })
    .eq("id", id);

  if (error) {
    console.error("Error archiving partner offer:", error);
    throw error;
  }
}

/**
 * Delete a partner offer (hard delete - use with caution)
 */
export async function deletePartnerOffer(id: UUID): Promise<void> {
  const { error } = await supabase.from("partner_offers").delete().eq("id", id);

  if (error) {
    console.error("Error deleting partner offer:", error);
    throw error;
  }
}

// =====================================================
// CATEGORIES
// =====================================================

/**
 * Get all categories (hierarchical)
 */
export async function getPartnerOfferCategories(): Promise<
  PartnerOfferCategory[]
> {
  const { data, error } = await supabase
    .from("partner_offer_categories")
    .select("*")
    .eq("is_active", true)
    .order("display_order", { ascending: true });

  if (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }

  // Build hierarchical structure
  const categories = data || [];
  const rootCategories = categories.filter((c) => !c.parent_id);
  const childCategories = categories.filter((c) => c.parent_id);

  return rootCategories.map((parent) => ({
    ...parent,
    children: childCategories.filter((child) => child.parent_id === parent.id),
  }));
}

/**
 * Create a new category
 */
export async function createCategory(
  name: string,
  slug: string,
  parentId?: UUID,
  description?: string,
): Promise<PartnerOfferCategory> {
  const { data, error } = await supabase
    .from("partner_offer_categories")
    .insert({
      name,
      slug,
      parent_id: parentId || null,
      description: description || null,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating category:", error);
    throw error;
  }

  return data;
}

/**
 * Update a category
 */
export async function updateCategory(
  id: UUID,
  updates: Partial<PartnerOfferCategory>,
): Promise<PartnerOfferCategory> {
  const { data, error } = await supabase
    .from("partner_offer_categories")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating category:", error);
    throw error;
  }

  return data;
}

// =====================================================
// STATE CLASSIFICATIONS
// =====================================================

/**
 * Get all state classifications
 */
export async function getStateClassifications(): Promise<
  StateInsuranceClassification[]
> {
  const { data, error } = await supabase
    .from("state_insurance_classifications")
    .select("*")
    .order("state_name", { ascending: true });

  if (error) {
    console.error("Error fetching state classifications:", error);
    throw error;
  }

  return data || [];
}

/**
 * Update a state classification
 */
export async function updateStateClassification(
  stateCode: string,
  classification: "insurance" | "non_insurance",
  notes?: string,
): Promise<StateInsuranceClassification> {
  const { data, error } = await supabase
    .from("state_insurance_classifications")
    .update({
      classification,
      notes: notes || null,
      updated_at: new Date().toISOString(),
    })
    .eq("state_code", stateCode)
    .select()
    .single();

  if (error) {
    console.error("Error updating state classification:", error);
    throw error;
  }

  return data;
}

// =====================================================
// EVENTS / ANALYTICS
// =====================================================

/**
 * Get events for an offer
 */
export async function getOfferEvents(
  offerId: UUID,
  limit = 100,
): Promise<PartnerOfferEvent[]> {
  const { data, error } = await supabase
    .from("partner_offer_events")
    .select("*")
    .eq("offer_id", offerId)
    .order("occurred_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching offer events:", error);
    throw error;
  }

  return data || [];
}

/**
 * Get event statistics for an offer
 */
export async function getOfferEventStats(offerId: UUID): Promise<{
  impressions: number;
  clicks: number;
  claims: number;
  redemptions: number;
}> {
  const { data, error } = await supabase
    .from("partner_offer_events")
    .select("event_type")
    .eq("offer_id", offerId);

  if (error) {
    console.error("Error fetching offer event stats:", error);
    throw error;
  }

  const events = data || [];
  return {
    impressions: events.filter((e) => e.event_type === "impression").length,
    clicks: events.filter((e) => e.event_type === "click").length,
    claims: events.filter((e) => e.event_type === "claim").length,
    redemptions: events.filter((e) => e.event_type === "redeem").length,
  };
}

// =====================================================
// API KEYS
// =====================================================

/**
 * Get all API keys
 */
export async function getApiKeys(): Promise<PartnerOfferApiKey[]> {
  const { data, error } = await supabase
    .from("partner_offer_api_keys")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching API keys:", error);
    throw error;
  }

  return data || [];
}

/**
 * Create a new API key
 * Returns the raw key (only shown once) and the key record
 */
export async function createApiKey(
  name: string,
  permissions: {
    can_create?: boolean;
    can_read?: boolean;
    can_update?: boolean;
    can_delete?: boolean;
  } = {},
): Promise<{ key: string; record: PartnerOfferApiKey }> {
  // Generate a secure random key
  const prefix = "po_live_";
  const randomBytes = new Uint8Array(24);
  crypto.getRandomValues(randomBytes);
  const randomPart = Array.from(randomBytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const rawKey = prefix + randomPart;
  const keyPrefix = rawKey.substring(0, 12);

  // Hash the key for storage
  const encoder = new TextEncoder();
  const keyData = encoder.encode(rawKey);
  const hashBuffer = await crypto.subtle.digest("SHA-256", keyData);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const keyHash = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  const { data, error } = await supabase
    .from("partner_offer_api_keys")
    .insert({
      name,
      key_hash: keyHash,
      key_prefix: keyPrefix,
      can_create: permissions.can_create ?? true,
      can_read: permissions.can_read ?? true,
      can_update: permissions.can_update ?? true,
      can_delete: permissions.can_delete ?? false,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating API key:", error);
    throw error;
  }

  return { key: rawKey, record: data };
}

/**
 * Revoke an API key
 */
export async function revokeApiKey(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("partner_offer_api_keys")
    .update({
      is_active: false,
      revoked_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    console.error("Error revoking API key:", error);
    throw error;
  }
}

// =====================================================
// ORGANIZATIONS (for source organization dropdown)
// =====================================================

interface OrganizationOption {
  id: UUID;
  name: string;
}

/**
 * Get organizations for dropdown selection
 */
export async function getOrganizationsForDropdown(): Promise<
  OrganizationOption[]
> {
  // Page through ALL active orgs — a single (unbounded) query cannot exceed
  // PostgREST's 1000-row cap, so this dropdown would silently truncate
  // alphabetically (prod has ~2,658 orgs). See fetchAllRows.
  try {
    return await fetchAllRows<OrganizationOption>((from, to) =>
      supabase
        .from("organizations")
        .select("id, name")
        .eq("status", "active")
        .eq("is_archived", false)
        .order("name", { ascending: true })
        .range(from, to),
    );
  } catch (error) {
    console.error("Error fetching organizations:", error);
    throw error;
  }
}

// =====================================================
// TAGS
// =====================================================

/**
 * Get all unique tags from offers
 */
export async function getAllTags(): Promise<string[]> {
  const { data, error } = await supabase.from("partner_offers").select("tags");

  if (error) {
    console.error("Error fetching tags:", error);
    throw error;
  }

  // Flatten and dedupe tags
  const allTags = new Set<string>();
  (data || []).forEach((offer) => {
    (offer.tags || []).forEach((tag: string) => allTags.add(tag));
  });

  return Array.from(allTags).sort();
}

/**
 * Delete a category (soft delete by setting is_active to false)
 */
export async function deleteCategory(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("partner_offer_categories")
    .update({ is_active: false })
    .eq("id", id);

  if (error) {
    console.error("Error deleting category:", error);
    throw error;
  }
}

// =====================================================
// STATISTICS
// =====================================================

/**
 * Get summary statistics for all offers
 * Note: For detailed analytics, external BI system should be used
 */
export async function getOfferStats(): Promise<{
  offers: {
    total: number;
    active: number;
    draft: number;
    scheduled: number;
    featured: number;
  };
  lifetime: {
    impressions: number;
    claims: number;
    conversionRate: string;
  };
  last7Days: {
    impressions: number;
    clicks: number;
    claims: number;
    redemptions: number;
  };
}> {
  // Get offer counts and totals
  const { data: offers, error: offersError } = await supabase
    .from("partner_offers")
    .select("id, status, is_featured, total_impressions, total_claims");

  if (offersError) {
    console.error("Error fetching offer stats:", offersError);
    throw offersError;
  }

  const allOffers = offers || [];
  const activeOffers = allOffers.filter((o) => o.status === "active");
  const draftOffers = allOffers.filter((o) => o.status === "draft");
  const scheduledOffers = allOffers.filter((o) => o.status === "scheduled");
  const featuredOffers = allOffers.filter((o) => o.is_featured);

  const totalImpressions = allOffers.reduce(
    (sum, o) => sum + (o.total_impressions || 0),
    0,
  );
  const totalClaims = allOffers.reduce(
    (sum, o) => sum + (o.total_claims || 0),
    0,
  );

  // Get recent events (last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const { data: recentEvents, error: eventsError } = await supabase
    .from("partner_offer_events")
    .select("event_type")
    .gte("occurred_at", sevenDaysAgo.toISOString());

  let last7Days = { impressions: 0, clicks: 0, claims: 0, redemptions: 0 };
  if (!eventsError && recentEvents) {
    last7Days = {
      impressions: recentEvents.filter((e) => e.event_type === "impression")
        .length,
      clicks: recentEvents.filter((e) => e.event_type === "click").length,
      claims: recentEvents.filter((e) => e.event_type === "claim").length,
      redemptions: recentEvents.filter((e) => e.event_type === "redeem").length,
    };
  }

  return {
    offers: {
      total: allOffers.length,
      active: activeOffers.length,
      draft: draftOffers.length,
      scheduled: scheduledOffers.length,
      featured: featuredOffers.length,
    },
    lifetime: {
      impressions: totalImpressions,
      claims: totalClaims,
      conversionRate:
        totalImpressions > 0
          ? ((totalClaims / totalImpressions) * 100).toFixed(2) + "%"
          : "0%",
    },
    last7Days,
  };
}
